import { Link } from 'react-router-dom';
import { Text } from '@clay-run/glaze/text';
import type { AccountHierarchyNode } from '@shared/flow/account-hierarchy';
import { DASH, hierarchyMoney } from './hierarchyPresentation';

/** Accounts listed before "+N more" (the legend list is a quick finder, not the table). */
const LEGEND_LIST_LIMIT = 50;

export interface LegendAccountListProps {
  accounts: readonly AccountHierarchyNode[];
  /** Builds a company's drilldown route (the shell owns the URL shape). */
  accountHref: (salesforceAccountId: string) => string;
  onShowInGraph: (id: string) => void;
  onShowInTable: (id: string) => void;
}

/** "ARR $1.2M", else "Pipe $45K", else the contact count: the one number that explains the status. */
function snippet(node: AccountHierarchyNode): string {
  if ((node.arr_active_subscriptions ?? 0) > 0) return `ARR ${hierarchyMoney(node.arr_active_subscriptions)}`;
  if ((node.open_pipeline ?? 0) > 0) return `Pipe ${hierarchyMoney(node.open_pipeline)}`;
  return `${node.contact_count} ${node.contact_count === 1 ? 'contact' : 'contacts'}`;
}

/** Which accounts the legend lens is highlighting, with a drilldown link and Show in graph / table. */
export function LegendAccountList({ accounts, accountHref, onShowInGraph, onShowInTable }: LegendAccountListProps) {
  const shown = accounts.slice(0, LEGEND_LIST_LIMIT);
  return (
    <div
      role="region"
      aria-label="Accounts in the selected statuses"
      data-testid="legend-account-list"
      className="max-h-40 w-full overflow-y-auto rounded-md border border-border-secondary bg-bg-primary"
    >
      <ul className="divide-y divide-border-secondary">
        {shown.map(n => (
          <li key={n.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-2 py-1 text-xs">
            <Link to={accountHref(n.id)} className="min-w-0 max-w-[16rem] truncate font-medium text-content-action hover:underline">
              {n.name ?? n.id}
            </Link>
            <span className="text-content-tertiary">{n.owner_name ?? DASH}</span>
            <span className="text-content-secondary">{snippet(n)}</span>
            <span className="ml-auto flex items-center gap-2">
              <button type="button" className="text-content-action hover:underline" onClick={() => onShowInGraph(n.id)}>Show in graph</button>
              <button type="button" className="text-content-action hover:underline" onClick={() => onShowInTable(n.id)}>Show in table</button>
            </span>
          </li>
        ))}
      </ul>
      {accounts.length > shown.length && (
        <Text size="xs" variant="tertiary" className="px-2 py-1">{`+${accounts.length - shown.length} more`}</Text>
      )}
    </div>
  );
}
